import React, { createContext, useContext, useEffect, useState } from 'react';
import { getGhostId, formatGhostId, supabase, isSupabaseEnabled } from './supabaseClient';

// Pluggable identity layer.
//
// The original Norwich Hub build minted a Ghost Citizen id from
// localStorage and used it as currentUser. Agoracle Global routes
// instead hand in a wallet address (via Thirdweb). Both paths share the
// same downstream machinery — Supabase profiles, pledges, realtime,
// leaderboard — so we abstract the source of truth behind an identity
// context and plug in a different provider per route.
//
//   <GhostIdentityProvider>    ...MultiPage...  </GhostIdentityProvider>
//   <WalletIdentityProvider>   ...MultiPage...  </WalletIdentityProvider>
//
// MultiPage just reads `useIdentity()` and renders whatever pill the
// provider supplies.

export type IdentityMode = 'ghost' | 'wallet';

// ── Trust Ladder ────────────────────────────────────────────────────────
//
// Canonical tiers are defined in PROTOCOL_SPEC.md §1 (Multiplier Engine)
// — this enum mirrors that spec. Enum values match the tier integers so
// `identity.tier === IdentityLevel.CITIZEN` reads naturally in gates.
//
//   Level 0 — SEEKER.     Sandbox / anonymous. Simulation-only (0.0×).
//   Level 1 — OBSERVER.   Standard wallet (1.0×).
//   Level 2 — CITIZEN.    Coinbase EAS Verified ID (1.5×).
//   Level 3 — SOVEREIGN.  Aged identity + high reputation (2.0× + rebates).
//
// P2P pledges on the Agoracle Global route require ≥ OBSERVER. CITIZEN
// unlocks the verified weight multiplier; SOVEREIGN adds further fee
// rebates. See PROTOCOL_SPEC.md for the EffectiveWeight formula.
export enum IdentityLevel {
  SEEKER    = 0,
  OBSERVER  = 1,
  CITIZEN   = 2,
  SOVEREIGN = 3,
}

// Kept as a narrow numeric alias so `Record<IdentityTier, …>` lookups
// stay tidy. New code may use the enum directly.
export type IdentityTier = IdentityLevel;

export interface TierMeta {
  label:     string;   // short uppercase banner label
  sublabel:  string;   // secondary descriptor
  icon:      string;   // emoji for the pill
  color:     string;   // primary tier colour (slate / bronze / gold)
  accent:    string;   // complementary accent (verified-green / sovereign-violet)
  bg:        string;   // pill background
  border:    string;   // pill border
  shadow?:   string;   // optional glow (Level 2)
}

export const TIER_META: Record<IdentityTier, TierMeta> = {
  // Level 0 — Seeker. Slate on slate; sandbox / anonymous observers.
  // Simulation only per PROTOCOL_SPEC.md (0.0× weight) — copy points
  // at the gate rather than nagging.
  [IdentityLevel.SEEKER]: {
    label:    'Seeker',
    sublabel: 'Sandbox · Simulation Only',
    icon:     '👤',
    color:    '#94a3b8',   // slate-400
    accent:   '#64748b',   // slate-500
    bg:       '#0f172a',
    border:   '#334155',
  },
  // Level 1 — Observer. Standard wallet holder. Bronze muted — the
  // citizen has skin in the game but hasn't yet verified.
  [IdentityLevel.OBSERVER]: {
    label:    'Observer',
    sublabel: 'Standard Wallet · 1.0× Weight',
    icon:     '🏺',
    color:    '#c2a15a',   // muted bronze
    accent:   '#78716c',   // stone — not-yet-verified
    bg:       '#1a130a',
    border:   '#78350f',
  },
  // Level 2 — Citizen. Coinbase EAS Verified ID. Bronze frame with a
  // green accent reading as a Proof-of-Personhood checkmark.
  [IdentityLevel.CITIZEN]: {
    label:    'Verified Citizen',
    sublabel: 'Coinbase EAS · 1.5× Weight',
    icon:     '🏛️',
    color:    '#f59e0b',   // bronze
    accent:   '#34d399',   // emerald — "verified"
    bg:       '#1a0f00',
    border:   '#92400e',
  },
  // Level 3 — Sovereign. Aged identity + high reputation. Gold frame
  // with a violet accent (sovereign tier) and a warm glow so the pill
  // visibly outranks Citizen.
  [IdentityLevel.SOVEREIGN]: {
    label:    'Sovereign',
    sublabel: 'Aged Identity · 2.0× + Rebates',
    icon:     '👑',
    color:    '#fde047',   // gold
    accent:   '#a78bfa',   // violet — sovereign
    bg:       '#140a1e',
    border:   '#7c3aed',
    shadow:   '0 0 12px rgba(253,224,71,0.35)',
  },
};

// ── Fee schedule ────────────────────────────────────────────────────────
//
// Basis-point-style schedule keyed by tier. Recorded on every pledge
// (Supabase pledges.trade_fee column) so monetisation analytics can run
// over historical rows once the Sovereign Exchange ships. The rebate
// floor attaches to Citizen (Verified ID) and Sovereign; Seeker /
// Observer pay the standard 1.0%. PROTOCOL_SPEC.md currently doesn't
// codify the fee ladder — this schedule inherits the pre-alignment
// numbers, and the spec should be extended in a follow-up to pin them.
export const TRADE_FEE_BY_TIER: Record<IdentityTier, number> = {
  [IdentityLevel.SEEKER]:    0.01,   // 1.0%
  [IdentityLevel.OBSERVER]:  0.01,   // 1.0%
  [IdentityLevel.CITIZEN]:   0.002,  // 0.2% — Verified ID rebate
  [IdentityLevel.SOVEREIGN]: 0.002,  // 0.2% — deeper rebate TBD per spec
};

export function tradeFeeForTier(tier: IdentityTier): number {
  return TRADE_FEE_BY_TIER[tier] ?? 0.01;
}

// ── Weight multipliers ──────────────────────────────────────────────────
//
// Canonical per PROTOCOL_SPEC.md §1:
//   EffectiveWeight = (Units × Price) × M
// where M comes from this table. Seeker pledges resolve to 0.0× —
// simulation-only — which means their staked OC is debited from the
// Dummy Pot but their contribution to resolution / reputation yield is
// zeroed out downstream. Observer is the unverified baseline (1.0×),
// Citizen the Verified ID rebate (1.5×), Sovereign the aged-identity
// apex (2.0×).
export const WEIGHT_MULTIPLIER_BY_TIER: Record<IdentityTier, number> = {
  [IdentityLevel.SEEKER]:    0.0,
  [IdentityLevel.OBSERVER]:  1.0,
  [IdentityLevel.CITIZEN]:   1.5,
  [IdentityLevel.SOVEREIGN]: 2.0,
};

export function weightMultiplierForTier(tier: IdentityTier): number {
  return WEIGHT_MULTIPLIER_BY_TIER[tier] ?? 1.0;
}

// Arena enrolment state — exposed alongside the Ghost id so the
// cockpit can drive the EnrolmentModal step machine and the
// GhostClaimModal off a single source of truth. Wallet-mode
// (Agoracle Global) providers leave this unset.
//
//   'ghost'              — no Supabase Auth session; pure Ghost mode.
//                          Show ENROL affordance to invite enrolment.
//   'pending_enrolment'  — auth session present but enrolled profile
//                          missing (post-magic-link, pre-username).
//                          Cockpit auto-opens EnrolmentModal at Step 2.
//   'enrolled'           — auth session + enrolled profile present.
//                          identity.id flips to the enrolled profile
//                          id (TEXT — the auth UUID as a string).
//
// See docs/audit/2026-05-14-arena-enrolment-brief.md §4.
export type EnrolmentStatus = 'ghost' | 'pending_enrolment' | 'enrolled';

export interface EnrolmentSnapshot {
  status:       EnrolmentStatus;
  ghostId:      string;              // always present — the citizen_XXXX from localStorage
  authUserId?:  string;              // Supabase auth.users.id when a session exists
  authEmail?:   string;              // email on the auth session
  enrolledId?:  string;              // profile.id of the enrolled row (auth UUID as TEXT)
  username?:    string;              // chosen username when enrolled
}

export interface Identity {
  id:         string | null;     // citizen_XXXX, enrolled profile id, 0x… — null when wallet mode with no wallet connected
  mode:       IdentityMode;
  connected:  boolean;           // true when `id` is usable
  tier:       IdentityTier;      // 0 Seeker / 1 Observer / 2 Citizen / 3 Sovereign
  refresh:    () => void;        // re-run the SBT/KYC probe (architect force hook)
  pill:       React.ReactNode;   // header control (Ghost/@username pill OR ConnectWallet)
  label:      string;            // short human-readable form for greeting copy
  enrolment?: EnrolmentSnapshot; // Arena enrolment state — unset on Wallet/Global mode
}

export const IdentityContext = createContext<Identity | null>(null);

export function useIdentity(): Identity {
  const ctx = useContext(IdentityContext);
  if (!ctx) throw new Error('useIdentity() called outside an IdentityProvider');
  return ctx;
}

// ── Trust probe (placeholder) ───────────────────────────────────────────
//
// The real implementation will chain:
//   1. Coinbase EAS attestation (Base Sepolia) → tier = CITIZEN (2)
//   2. Aged-identity + reputation probe        → tier = SOVEREIGN (3)
//
// Today we recognise two overrides so the architect can test the ladder
// end-to-end without either provider wired up:
//
//   localStorage.agoracle_identity_tier   = '0' | '1' | '2' | '3'   ← preferred
//   localStorage.agoracle_citizen_seal    = '1'                     ← legacy
//     (Trust Phase v1 "citizen seal" flag — maps to the new CITIZEN
//      tier 2 since both represented verified personhood)
//
// `setForcedTier` persists the override and fires a window event so any
// live provider re-runs its probe without a page reload. `window.__agoracleForceTier`
// is exposed for console-based forcing by the Architect during demos.
export const CITIZEN_SEAL_CONTRACT =
  '0x0000000000000000000000000000000000000000'; // placeholder until the SBT ships on Base

const FORCE_EVENT = 'agoracle:tier-change';
// Fired from the cockpit after EnrolmentModal or GhostClaimModal
// completes so the identity provider re-reads the enrolled profile
// state. Auth-state-change is not enough on its own because flipping
// public.profiles.enrolment_state from 'ghost' to 'enrolled' doesn't
// generate a Supabase Auth event — the auth.users row is unchanged.
const ENROLMENT_REFRESH_EVENT = 'agoracle:enrolment-refresh';

export function dispatchEnrolmentRefresh(): void {
  if (typeof window === 'undefined') return;
  try { window.dispatchEvent(new Event(ENROLMENT_REFRESH_EVENT)); } catch { /* never throws */ }
}

function readForcedTier(): IdentityTier | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem('agoracle_identity_tier');
    if (raw !== null) {
      const n = Number(raw);
      if (n === 0 || n === 1 || n === 2 || n === 3) return n as IdentityTier;
    }
    if (window.localStorage.getItem('agoracle_citizen_seal') === '1') {
      return IdentityLevel.CITIZEN;
    }
  } catch { /* storage denied */ }
  return null;
}

export function setForcedTier(tier: IdentityTier | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (tier === null) window.localStorage.removeItem('agoracle_identity_tier');
    else               window.localStorage.setItem('agoracle_identity_tier', String(tier));
    window.dispatchEvent(new Event(FORCE_EVENT));
  } catch { /* storage denied */ }
}

export async function refreshSBTStatus(_address: string | null): Promise<IdentityTier> {
  // ── SANDBOX BRANCH (ui-sandbox) ───────────────────────────────────────
  // Default every citizen to OBSERVER (1.0× weight) so UI testers can
  // pledge without wiring Coinbase EAS / MetaMask. The sandbox Power-Up
  // button promotes them to CITIZEN (1.5×) via setForcedTier; an
  // architect override still wins so the GodModePanel tier-force strip
  // exercises Seeker / Sovereign too.
  const forced = readForcedTier();
  if (forced !== null) return forced;
  return IdentityLevel.OBSERVER;
}

// Back-compat shim for the single-boolean "is this citizen verified"
// check. Under the new ladder "verified" means CITIZEN (Coinbase EAS)
// or higher. New code should consume `identity.tier` directly.
export async function checkCitizenSeal(address: string | null): Promise<boolean> {
  return (await refreshSBTStatus(address)) >= IdentityLevel.CITIZEN;
}

// Expose a console helper so the Architect can force the tier without
// juggling localStorage by hand. Safe to register in any environment;
// no-op on SSR.
if (typeof window !== 'undefined') {
  (window as unknown as { __agoracleForceTier?: (t: IdentityTier | null) => void }).__agoracleForceTier = setForcedTier;
}

// ── Providers ───────────────────────────────────────────────────────────

// Ghost Mode: per-browser citizen_ id from localStorage. Preserves the
// original Norwich Hub behaviour for cold visitors — no wallet
// required. With the Arena enrolment surface (brief 2026-05-14), the
// same provider also resolves a Supabase Auth session if present: an
// enrolled identity supersedes the Ghost id without forfeiting the
// historical Ghost provenance (claims are recorded in
// public.identity_claims; the Ghost row stays untouched).
//
// Ghosts are Level 0 by definition (no on-chain binding), but we
// still honour the architect override for local demos of the verified
// UI.
export function GhostIdentityProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [ghostId] = useState<string>(() => getGhostId());
  // v2.4 — hydrate from the forced-tier localStorage key synchronously
  // so a returning Citizen on mobile doesn't flash as Observer on the
  // first paint before refreshSBTStatus resolves. Any mismatch is
  // corrected when runProbe lands a few ms later.
  const [tier, setTier] = useState<IdentityTier>(() => readForcedTier() ?? 0);

  // Enrolment snapshot — starts as pure Ghost; the auth-session
  // effect below replaces it once we know the truth from Supabase.
  const [enrolment, setEnrolment] = useState<EnrolmentSnapshot>(
    () => ({ status: 'ghost', ghostId }),
  );

  const runProbe = React.useCallback(() => {
    let cancelled = false;
    refreshSBTStatus(ghostId).then(t => { if (!cancelled) setTier(t); });
    return () => { cancelled = true; };
  }, [ghostId]);

  useEffect(() => {
    const cancel = runProbe();
    window.addEventListener(FORCE_EVENT, runProbe);
    return () => {
      cancel();
      window.removeEventListener(FORCE_EVENT, runProbe);
    };
  }, [runProbe]);

  // ── Arena enrolment session resolver ───────────────────────────────
  // Mount-time: ask Supabase whether there's an active auth session.
  // If yes, look up the profile row keyed on auth_user_id and decide
  // whether the user has finished enrolment (Step 2 username +
  // T&Cs). Listen for auth state changes so the magic-link
  // round-trip flips state without a reload.
  useEffect(() => {
    if (!isSupabaseEnabled() || !supabase) return;
    const sb = supabase;
    let cancelled = false;

    async function resolveSession(): Promise<void> {
      const { data: { session }, error: sessionErr } = await sb.auth.getSession();
      if (cancelled) return;

      if (sessionErr) {
        console.warn('[GhostIdentityProvider] getSession returned error:', sessionErr);
      }
      console.log('[GhostIdentityProvider] session check:', {
        hasSession:  !!session?.user,
        authUserId:  session?.user?.id,
        authEmail:   session?.user?.email,
        urlHash:     typeof window !== 'undefined' ? window.location.hash.slice(0, 64) : '(ssr)',
        urlSearch:   typeof window !== 'undefined' ? window.location.search.slice(0, 64) : '(ssr)',
      });

      if (!session?.user) {
        setEnrolment({ status: 'ghost', ghostId });
        return;
      }

      const authUserId = session.user.id;
      const authEmail  = session.user.email ?? undefined;

      const { data: rows, error } = await sb
        .from('profiles')
        .select('id, username, enrolment_state')
        .eq('auth_user_id', authUserId)
        .limit(1);
      if (cancelled) return;

      if (error) {
        console.error('[GhostIdentityProvider] enrolment lookup failed', error);
        setEnrolment({ status: 'pending_enrolment', ghostId, authUserId, authEmail });
        return;
      }

      const row = (rows ?? [])[0] as
        | { id: string; username: string | null; enrolment_state: string }
        | undefined;

      console.log('[GhostIdentityProvider] enrolled profile lookup:', {
        authUserId,
        rowFound:        !!row,
        enrolmentState:  row?.enrolment_state,
        username:        row?.username,
      });

      if (row && row.enrolment_state === 'enrolled') {
        setEnrolment({
          status:      'enrolled',
          ghostId,
          authUserId,
          authEmail,
          enrolledId:  row.id,
          username:    row.username ?? undefined,
        });
      } else {
        setEnrolment({ status: 'pending_enrolment', ghostId, authUserId, authEmail });
      }
    }

    void resolveSession();

    const { data: sub } = sb.auth.onAuthStateChange((event, nextSession) => {
      // Re-run the lookup on every state change — covers magic-link
      // return, manual sign-out, token refresh.
      console.log('[GhostIdentityProvider] auth state change:', {
        event,
        hasSession: !!nextSession?.user,
        authEmail:  nextSession?.user?.email,
      });
      void resolveSession();
    });

    const onRefresh = (): void => { void resolveSession(); };
    window.addEventListener(ENROLMENT_REFRESH_EVENT, onRefresh);

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
      window.removeEventListener(ENROLMENT_REFRESH_EVENT, onRefresh);
    };
  }, [ghostId]);

  const isEnrolled = enrolment.status === 'enrolled' && enrolment.enrolledId !== undefined;
  const activeId   = isEnrolled ? (enrolment.enrolledId as string) : ghostId;
  const label      = isEnrolled && enrolment.username
    ? `@${enrolment.username}`
    : formatGhostId(ghostId);

  const pill = isEnrolled ? (
    <span
      title={enrolment.authEmail ?? enrolment.username ?? ''}
      style={{
        backgroundColor: '#1e1b4b',
        border:          '1px solid #6366f1',
        borderRadius:    12,
        color:           '#e0e7ff',
        fontSize:        10,
        padding:         '3px 10px',
        fontFamily:      'monospace',
        letterSpacing:   '0.08em',
        fontWeight:      700,
      }}>
      {label}
    </span>
  ) : (
    <span
      title={ghostId}
      style={{
        backgroundColor: '#1e1b4b',
        border:          '1px solid #4338ca',
        borderRadius:    12,
        color:           '#c7d2fe',
        fontSize:        10,
        padding:         '3px 10px',
        fontFamily:      'monospace',
        letterSpacing:   '0.08em',
      }}>
      {formatGhostId(ghostId)}
    </span>
  );

  const value: Identity = {
    id:        activeId,
    mode:      'ghost',
    connected: true,
    tier,
    refresh:   runProbe,
    pill,
    label,
    enrolment,
  };

  return (
    <IdentityContext.Provider value={value}>
      {children}
    </IdentityContext.Provider>
  );
}
